import '@mantine/core/styles.css';
import '@fontsource/instrument-sans/400.css';
import '@fontsource/instrument-sans/500.css';
import '@fontsource/instrument-sans/600.css';
import '@fontsource/instrument-sans/700.css';
import '@mantine/notifications/styles.css';
import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { createAppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { WagmiProvider, fallback, http } from 'wagmi';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { rangeFromZero } from 'src/utils';
import { NETWORK_INFO, RECOMMEND_HAHA_WALLET } from 'src/constants';
import { Router } from './Router';
import { theme } from './theme';
import { ConfigContext } from './ConfigContext';
import { projectConfigs } from './projectConfigs';
import './global.scss';

const themeVariable = import.meta.env.VITE_COLOR_THEME || '';
const config = (projectConfigs[themeVariable] || projectConfigs.default)!;
declare global {
  interface Window {
    haha: any;
  }
}

const projectId = import.meta.env.VITE_WALLET_CONNECT_PROJECT_ID || '';

const metadata = {
  name: 'Capricorn Staking',
  description: 'MEV-powered liquid staking on Monad',
  url: 'https://app.capricorn.tech',
  icons: ['https://app.capricorn.tech/capricorn-symbol.svg'],
};

const wagmiAdapter = new WagmiAdapter({
  networks: [NETWORK_INFO],
  projectId,
  transports: {
    [NETWORK_INFO.id]: fallback(
      NETWORK_INFO.rpcUrls.default.http.map((url) =>
        http(url, { batch: { wait: 100 }, retryCount: 0 })
      )
    ),
  },
});

createAppKit({
  adapters: [wagmiAdapter],
  networks: [NETWORK_INFO],
  metadata,
  projectId,
  themeMode: 'dark',
  themeVariables: {
    '--w3m-z-index': 1000,
  },
  features: {
    analytics: false,
    email: false,
    socials: false,
    swaps: false,
    onramp: false,
    send: false,
    history: false,
  },
  featuredWalletIds: [
    '8a0ee50d1f22f6651afcae7eb4253e52a3310b90af5daef78a8c4929a9bb99d4', // binance
    '971e689d0a5be527bac79629b4ee9b925e82208e5168b733496a09c0faed0709', // okx
    '2bd8c14e035c2d48f184aaa168559e86b0e3433228d3c4075900a221785019b0', // backpack
    ...(RECOMMEND_HAHA_WALLET && !window.haha
      ? ['719bd888109f5e8dd23419b20e749900ce4d2fc6858cf588395f19c82fd036b3']
      : []),
  ],
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchInterval: 1000 * 30,
      retry: false,
    },
  },
});

export default function App() {
  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>
        <MantineProvider
          theme={theme}
          cssVariablesResolver={() => {
            return {
              light: {},
              dark: {},
              variables: {
                ...rangeFromZero(10).reduce(
                  (acc, i) => {
                    acc[`--mantine-color-main-${i}`] =
                      `var(--mantine-color-dark-${i})`;
                    return acc;
                  },
                  {} as Record<string, string>
                ),
              },
            };
          }}
          forceColorScheme="dark"
        >
          <ModalsProvider>
            <ConfigContext.Provider value={config}>
              <Router />
            </ConfigContext.Provider>
            <Notifications
              position="bottom-right"
              zIndex={1000}
              autoClose={10000}
              limit={5}
            />
          </ModalsProvider>
        </MantineProvider>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
